import { getCountryName } from '@mahawi/eshop-common/src/get-country-name';
import { getFlag } from '@mahawi/eshop-common/src/get-flag';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { Space, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import { type Key } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import UpdatedAt from '../components/updated-at';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { getTagByRelativeDate } from '../fragments/get-tag-by-relative-date';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { crmCustomersLoadAll } from '../reducers/crm-customers/reducer';
import { type ICustomerAdmin } from '../reducers/crm-customers/types';
import { COLOR, SPACING } from '../theme/tokens';

const { Text } = Typography;

interface DataType extends ICustomerAdmin {
  key: string;
}

function CrmCustomersContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const customers = useSelector((s: RootState) => s.CrmCustomers.customers);
  const inProcess = useSelector((s: RootState) => s.CrmCustomers.inProcess);
  const updatedAt = useSelector((s: RootState) => s.CrmCustomers.updatedAt);

  const [columns, setColumns] = useState<ColumnsType>([]);
  const [dataSource, setDataSource] = useState<DataType[]>([]);

  useEffect((): void => {
    const dataSourceUE: DataType[] | undefined = customers?.map(
      (c: ICustomerAdmin): DataType => ({
        ...c,
        key: c.uuid,
      }),
    );

    const columnsUE: ColumnsType = [
      {
        title: 'Email',
        dataIndex: 'email',
        render: (
          email: string | undefined,
          record: DataType,
        ): React.ReactElement => (
          <Link
            to={`/crm/customer/${record.uuid}`}
            label={email ?? record.uuid}
            openInNewTab
          />
        ),
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.email ?? '', b.email ?? ''),
        ...getColumnSearch('email'),
      },
      {
        title: 'First Name',
        dataIndex: 'firstName',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.firstName ?? '', b.firstName ?? ''),
        ...getColumnSearch('firstName'),
      },
      {
        title: 'Last Name',
        dataIndex: 'lastName',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.lastName ?? '', b.lastName ?? ''),
        ...getColumnSearch('lastName'),
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        ...getColumnSearch('phone'),
      },
      {
        title: 'City',
        dataIndex: 'city',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.city ?? '', b.city ?? ''),
        ...getColumnSearch('city'),
      },
      {
        title: 'Country',
        dataIndex: 'country',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.country ?? '', b.country ?? ''),
        filters: [
          ...new Set(
            dataSourceUE
              ?.map(({ country }: DataType): string | undefined => country)
              .filter((c): c is string => !!c),
          ),
        ]
          .sort()
          .map((c: string) => ({
            text: `${getFlag({ countryTypeIsoCode: c })} ${getCountryName({ countryTypeIsoCode: c })} (${c})`,
            value: c,
          })),
        onFilter: (value: boolean | Key, record: DataType): boolean =>
          record.country === value,
        render: (c: string | undefined): string =>
          c
            ? `${getFlag({ countryTypeIsoCode: c })} ${getCountryName({ countryTypeIsoCode: c })} (${c})`
            : EMPTY_VALUE,
      },
      {
        title: 'Fakturoid ID',
        dataIndex: 'fakturoidId',
        sorter: (a: DataType, b: DataType): number =>
          (a.fakturoidId ?? 0) - (b.fakturoidId ?? 0),
        filters: [
          { text: 'Set', value: 'set' },
          { text: 'Not set', value: 'notset' },
        ],
        onFilter: (value: boolean | Key, record: DataType): boolean =>
          value === 'set' ? !!record.fakturoidId : !record.fakturoidId,
      },
      {
        title: 'Orders',
        dataIndex: 'orderCount',
        sorter: (a: DataType, b: DataType): number =>
          a.orderCount - b.orderCount,
        render: (count: number): React.ReactElement => {
          const color =
            count >= 5
              ? COLOR.success
              : count >= 2
                ? COLOR.warning
                : COLOR.error;
          return <Text style={{ color }}>{count}</Text>;
        },
      },
      {
        title: 'Last Order',
        dataIndex: 'lastOrderAt',
        sorter: (a: DataType, b: DataType): number =>
          dayjs(a.lastOrderAt ?? 0).unix() - dayjs(b.lastOrderAt ?? 0).unix(),
        render: (v: string | undefined): React.ReactElement =>
          v ? (
            getTagByRelativeDate({ date: new Date(v) })
          ) : (
            <Text type="secondary">{EMPTY_VALUE}</Text>
          ),
      },
    ];

    setColumns(columnsUE);

    if (!dataSourceUE) {
      return;
    }

    setDataSource(dataSourceUE);
  }, [customers]);

  return (
    <Space orientation="vertical" size={SPACING.lg} style={{ width: '100%' }}>
      <DetailPageHeader
        title="Customers"
        onReload={(): void => {
          dispatch(crmCustomersLoadAll());
        }}
        isReloadDisabled={inProcess}
        isReloadHighlighted={!customers}
        info={
          <>
            <UpdatedAt value={updatedAt} />
            {customers && (
              <Typography.Text type="secondary">
                {customers.length} customers
              </Typography.Text>
            )}
          </>
        }
      />

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={inProcess && !customers}
        scroll={{ x: true }}
      />
    </Space>
  );
}

export default CrmCustomersContainer;
